import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../utils/supabaseClient';
import {
  safeExchangeCodeForSession,
  safeUpdateUserPassword,
  withTimeout,
} from '../../utils/supabaseAuthSafe';

const RECOVERY_PARAM_NAMES = [
  'code',
  'token_hash',
  'type',
  'access_token',
  'refresh_token',
  'error',
  'error_description',
];

const getQueryParam = (name) => {
  try {
    return new URLSearchParams(window.location.search).get(name);
  } catch {
    return null;
  }
};

const parseHashParams = () => {
  const hash = typeof window !== 'undefined' ? window.location.hash : '';
  if (!hash || !hash.startsWith('#')) return {};
  const qs = hash.slice(1);
  const params = new URLSearchParams(qs);
  const out = {};
  for (const [k, v] of params.entries()) out[k] = v;
  return out;
};

const stripRecoveryParamsFromUrl = () => {
  if (typeof window === 'undefined') return;
  try {
    const url = new URL(window.location.href);
    RECOVERY_PARAM_NAMES.forEach((param) => {
      url.searchParams.delete(param);
    });
    // Recovery tokens in hash should never remain in browser history.
    url.hash = '';
    const nextSearch = url.searchParams.toString();
    const nextPath = `${url.pathname}${nextSearch ? `?${nextSearch}` : ''}`;
    window.history.replaceState({}, document.title, nextPath);
  } catch {
    // ignore URL parsing failures
  }
};

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const navigateTimeoutRef = useRef(null);
  const [isExchanging, setIsExchanging] = useState(true);
  const [exchangeError, setExchangeError] = useState('');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const hasPasswordMismatch = useMemo(() => {
    if (!password || !confirmPassword) return false;
    return password !== confirmPassword;
  }, [password, confirmPassword]);

  useEffect(() => {
    const run = async () => {
      setIsExchanging(true);
      setExchangeError('');

      try {
        // Newer PKCE flow: /auth/reset?code=... (recommended)
        const code = getQueryParam('code');
        if (code) {
          const { error: exErr } = await safeExchangeCodeForSession(supabase, {
            code,
            timeoutMs: 10000,
          });
          if (exErr) throw exErr;
          stripRecoveryParamsFromUrl();
          return;
        }

        // Older implicit flow: tokens in URL hash (#access_token=...&refresh_token=...)
        const hash = parseHashParams();
        if (hash.access_token && hash.refresh_token) {
          const accessToken = hash.access_token;
          const refreshToken = hash.refresh_token;
          stripRecoveryParamsFromUrl();
          const { error: setErr } = await withTimeout(
            supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken,
            }),
            10000,
            'supabase.auth.setSession(recovery)'
          );
          if (setErr) throw setErr;
          return;
        }

        throw new Error(
          'Missing recovery parameters. Please use the password reset link from your email again.'
        );
      } catch (err) {
        setExchangeError(err?.message || 'Failed to start password reset.');
      } finally {
        setIsExchanging(false);
      }
    };

    run();
  }, []);

  useEffect(() => {
    return () => {
      if (navigateTimeoutRef.current) {
        window.clearTimeout(navigateTimeoutRef.current);
        navigateTimeoutRef.current = null;
      }
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setIsSubmitting(true);
    try {
      const { error: updateError } = await safeUpdateUserPassword(supabase, {
        password,
        timeoutMs: 10000,
      });
      if (updateError) throw updateError;

      setMessage('Password updated! You can now log in with your new password.');

      // Optional: navigate to auth page after a short delay.
      if (navigateTimeoutRef.current) {
        window.clearTimeout(navigateTimeoutRef.current);
      }
      navigateTimeoutRef.current = window.setTimeout(() => {
        navigate('/auth');
      }, 800);
    } catch (err) {
      if (err?.code === 'TIMEOUT') {
        setError(
          'Update timed out. Please check your connection/VPN/adblock and try again.'
        );
      } else {
        setError(err?.message || 'Failed to update password.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-card">
        <h2>Choose a new password</h2>
        <p className="auth-subtitle">Use a strong password you do not reuse.</p>

        {isExchanging ? (
          <div className="auth-message">Loading reset session...</div>
        ) : exchangeError ? (
          <div className="auth-error">{exchangeError}</div>
        ) : (
          <form onSubmit={handleSubmit} className="auth-form">
            <label className="auth-field">
              New password
              <input
                className="auth-input"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </label>
            <label className="auth-field">
              Confirm new password
              <input
                className="auth-input"
                type="password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
            </label>

            {hasPasswordMismatch && (
              <div className="auth-error">Passwords do not match.</div>
            )}

            <button className="auth-button" type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Please wait...' : 'Update password'}
            </button>
          </form>
        )}

        {error && <div className="auth-error">{error}</div>}
        {message && <div className="auth-message">{message}</div>}
      </div>
    </div>
  );
};

export default ResetPasswordPage;
